import { useCallback, useEffect, useState } from "react";
import { useAuth, formatErr } from "../lib/auth";
import api from "../lib/api";
import TopNav from "../components/TopNav";
import {
  Building2, Copy, Plus, ShieldOff, Mail, Calendar, Users, BadgeCheck, Loader2, Inbox,
} from "lucide-react";

const TRACKS = [
  { id: "operator", name: "Operator" },
  { id: "engineer", name: "Engineer" },
  { id: "hunter", name: "Hunter / Analyst" },
  { id: "leader", name: "Leader" },
];

const STATUS_TONE = {
  active: { color: "var(--green)", label: "Active" },
  redeemed: { color: "var(--cyan)", label: "Redeemed" },
  expired: { color: "var(--amber)", label: "Expired" },
  revoked: { color: "var(--red)", label: "Revoked" },
};

export default function Org() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({ role: "trainee", track: "operator", email: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [o, i, m] = await Promise.all([
        api.get("/me/org"),
        api.get("/me/org/invites"),
        api.get("/me/org/members"),
      ]);
      setOrg(o.data);
      setInvites(i.data);
      setMembers(m.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || "Could not load org.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createInvite = async (e) => {
    e.preventDefault();
    setBusy(true); setCreated(null); setErr("");
    try {
      const body = { role: form.role, track: form.track };
      if (form.email.trim()) body.email = form.email.trim();
      const { data } = await api.post("/me/org/invites", body);
      setCreated(data);
      setForm({ role: "trainee", track: "operator", email: "" });
      load();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? Anyone holding the link will be blocked.")) return;
    try {
      await api.delete(`/me/org/invites/${id}`);
      load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch (e) { console.error(e); }
  };

  const isOrgAdmin = user?.role === "org_admin" || user?.role === "admin";

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="overline">// ORG_CONTROL</div>
        <h1 className="font-display font-bold text-4xl uppercase mt-2">Squadron HQ</h1>
        <p className="text-sm text-[var(--text-2)] mt-2 max-w-2xl">
          Manage members, mint invite codes, and watch your trial clock. Only org admins see this page.
        </p>

        {!isOrgAdmin && (
          <div className="mt-6 flat-card p-6 border border-[var(--amber)]" data-testid="org-no-access">
            <div className="font-display uppercase text-lg">Org admin only</div>
            <p className="text-sm text-[var(--text-2)] mt-2">Ask your squadron leader for an invite.</p>
          </div>
        )}

        {isOrgAdmin && loading && (
          <div className="mt-10 flex items-center gap-2 text-[var(--text-2)] font-mono text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {isOrgAdmin && !loading && org && (
          <div className="mt-8 space-y-8">
            <OrgSummaryCard org={org} />

            {user?.role === "admin" && <LeadsPanel />}

            {err && (
              <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-3" data-testid="org-error">
                {err}
              </div>
            )}

            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5">
                <NewInviteCard form={form} setForm={setForm} busy={busy} submit={createInvite} />
                {created && <NewInviteResult invite={created} onCopy={copy} />}
              </div>

              <div className="lg:col-span-7 space-y-6">
                <InviteList invites={invites} onRevoke={revoke} onCopy={copy} />
                <MemberList members={members} />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );

  // ---- Local components below for layout cohesion only ----

  function OrgSummaryCard({ org }) {
    const trialDaysLeft = org.trial_ends_at
      ? Math.max(0, Math.ceil((new Date(org.trial_ends_at) - new Date()) / 86400000))
      : null;
    return (
      <div className="flat-card p-6 grid sm:grid-cols-4 gap-px bg-[var(--border)]" data-testid="org-summary">
        <div className="bg-[var(--bg)] p-5">
          <div className="overline">// ORG</div>
          <div className="font-display text-xl uppercase mt-1">{org.name}</div>
          <div className="font-mono text-[10px] text-[var(--text-2)] mt-1">{org.slug}</div>
        </div>
        <div className="bg-[var(--bg)] p-5">
          <div className="overline">// TIER</div>
          <div className="font-display text-xl uppercase mt-1">{org.subscription_tier}</div>
          <div className="font-mono text-[10px] text-[var(--text-2)] mt-1 uppercase">{org.subscription_status}</div>
        </div>
        <div className="bg-[var(--bg)] p-5">
          <div className="overline">// SEATS</div>
          <div className="font-mono text-2xl text-[var(--cyan)] mt-1">{org.seats_used} / {org.seat_limit}</div>
          <div className="font-mono text-[10px] text-[var(--text-2)] mt-1">{Math.max(0, org.seat_limit - org.seats_used)} open</div>
        </div>
        <div className="bg-[var(--bg)] p-5">
          <div className="overline">// TRIAL</div>
          <div className="font-mono text-2xl mt-1" style={{ color: trialDaysLeft != null && trialDaysLeft <= 7 ? "var(--amber)" : "var(--text-1)" }}>
            {trialDaysLeft == null ? "∞" : `${trialDaysLeft}d`}
          </div>
          <div className="font-mono text-[10px] text-[var(--text-2)] mt-1">
            {org.trial_ends_at ? "until trial ends" : "no expiry"}
          </div>
        </div>
      </div>
    );
  }

  function NewInviteCard({ form, setForm, busy, submit }) {
    return (
      <div className="flat-card p-6" data-testid="new-invite-card">
        <div className="flex items-center gap-2 mb-3">
          <Plus size={18} color="var(--cyan)" strokeWidth={1.5} />
          <h2 className="font-display font-bold text-xl uppercase">Mint Invite</h2>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="input-tac mt-1" data-testid="invite-role">
              <option value="trainee">Trainee</option>
              <option value="leader">Leader</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Track</label>
            <select value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })}
                    className="input-tac mt-1" data-testid="invite-track">
              {TRACKS.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Email (optional)</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="input-tac mt-1" placeholder="ops@partner.com.au" data-testid="invite-email" />
            <p className="font-mono text-[10px] text-[var(--text-2)] mt-1">If set, the invite is locked to this address.</p>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="invite-create">
            {busy ? "Minting…" : "Mint Invite Code"}
          </button>
        </form>
      </div>
    );
  }

  function NewInviteResult({ invite, onCopy }) {
    return (
      <div className="mt-4 flat-card p-5 border border-[var(--cyan)]" data-testid="invite-created">
        <div className="overline">// READY_TO_SEND</div>
        <div className="font-mono text-sm text-white mt-2 break-all">{invite.redeem_url}</div>
        <div className="mt-3 flex gap-2">
          <button onClick={() => onCopy(invite.redeem_url)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="invite-copy-url">
            <Copy size={12} /> Copy link
          </button>
          <button onClick={() => onCopy(invite.code)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="invite-copy-code">
            <Copy size={12} /> Copy code
          </button>
        </div>
      </div>
    );
  }

  function InviteList({ invites, onRevoke, onCopy }) {
    return (
      <div className="flat-card p-6" data-testid="invite-list">
        <div className="flex items-center gap-2 mb-4">
          <Mail size={18} color="var(--cyan)" strokeWidth={1.5} />
          <h2 className="font-display font-bold text-xl uppercase">Invites</h2>
          <span className="font-mono text-xs text-[var(--text-2)]">// {invites.length} total</span>
        </div>
        {invites.length === 0 ? (
          <p className="text-sm text-[var(--text-2)] font-mono">// no invites yet — mint one to onboard a teammate.</p>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {invites.map((inv) => {
              const tone = STATUS_TONE[inv.status] || STATUS_TONE.active;
              return (
                <div key={inv.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`invite-row-${inv.id}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-white truncate">
                      {inv.email || <span className="text-[var(--text-2)]">// open invite</span>}
                    </div>
                    <div className="font-mono text-[10px] text-[var(--text-2)] mt-1">
                      {inv.role} · {inv.track} · expires {new Date(inv.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="tag" style={{ color: tone.color, borderColor: tone.color }}>{tone.label}</span>
                    {inv.status === "active" && (
                      <>
                        <button onClick={() => onCopy(inv.redeem_url)} className="btn-ghost text-xs" title="Copy redeem URL" data-testid={`invite-row-copy-${inv.id}`}>
                          <Copy size={12} />
                        </button>
                        <button onClick={() => onRevoke(inv.id)} className="btn-ghost text-xs text-[var(--red)] border-[var(--red)]" title="Revoke" data-testid={`invite-row-revoke-${inv.id}`}>
                          <ShieldOff size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function MemberList({ members }) {
    return (
      <div className="flat-card p-6" data-testid="member-list">
        <div className="flex items-center gap-2 mb-4">
          <Users size={18} color="var(--cyan)" strokeWidth={1.5} />
          <h2 className="font-display font-bold text-xl uppercase">Members</h2>
          <span className="font-mono text-xs text-[var(--text-2)]">// {members.length} active</span>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {members.map((m) => (
            <div key={m.id} className="py-3 flex items-center justify-between" data-testid={`member-row-${m.id}`}>
              <div>
                <div className="font-mono text-sm text-white">{m.name}</div>
                <div className="font-mono text-[10px] text-[var(--text-2)] mt-1">{m.email} · {m.track}</div>
              </div>
              <span className="tag" style={{
                color: m.role === "org_admin" ? "var(--cyan)" : m.role === "leader" ? "var(--amber)" : "var(--text-1)",
                borderColor: m.role === "org_admin" ? "var(--cyan)" : m.role === "leader" ? "var(--amber)" : "var(--border)",
              }}>
                {m.role === "org_admin" ? <><BadgeCheck size={10} className="inline mr-1" />ORG ADMIN</> :
                 m.role === "leader" ? "LEADER" : m.role === "admin" ? "PLATFORM" : "TRAINEE"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function LeadsPanel() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const [l, s] = await Promise.all([
            api.get("/admin/leads?limit=30"),
            api.get("/admin/leads/stats"),
          ]);
          if (!cancelled) { setLeads(l.data); setStats(s.data); }
        } catch (e) {
          console.error(e);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }, []);

    return (
      <div className="flat-card p-6" data-testid="leads-panel">
        <div className="flex items-center gap-2 mb-4">
          <Inbox size={18} color="var(--cyan)" strokeWidth={1.5} />
          <h2 className="font-display font-bold text-xl uppercase">Leads</h2>
          <span className="font-mono text-xs text-[var(--text-2)]">// sample-pack prospects · platform admin only</span>
        </div>

        {stats && (
          <div className="grid grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)] mb-5" data-testid="leads-stats">
            {[
              ["TOTAL", stats.total],
              ["UNIQUE", stats.distinct_emails],
              ["LAST 24H", stats.last_24h],
              ["LAST 7D", stats.last_7d],
            ].map(([label, val]) => (
              <div key={label} className="bg-[var(--bg)] p-3">
                <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">{label}</div>
                <div className="font-mono text-xl text-[var(--cyan)] mt-1">{val}</div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="font-mono text-xs text-[var(--text-2)] flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading prospects…</div>
        ) : leads.length === 0 ? (
          <p className="text-sm text-[var(--text-2)] font-mono">// no prospects yet — share defdossier.com to start filling the funnel.</p>
        ) : (
          <div className="divide-y divide-[var(--border)]" data-testid="leads-list">
            {leads.map((l) => {
              const ts = l.requested_at ? new Date(l.requested_at) : null;
              const minutesAgo = ts ? Math.round((Date.now() - ts.getTime()) / 60000) : null;
              const ago = minutesAgo == null ? "" :
                minutesAgo < 60 ? `${minutesAgo}m ago` :
                minutesAgo < 1440 ? `${Math.round(minutesAgo / 60)}h ago` :
                `${Math.round(minutesAgo / 1440)}d ago`;
              const isExtension = l.source === "trial_extension_request";
              const mailto = isExtension ? `mailto:${l.email}?subject=${encodeURIComponent(
                `Re: DefDossier trial extension — ${l.org || "your team"}`
              )}&body=${encodeURIComponent(
                `Hi ${l.name?.split(" ")[0] || "there"},\n\n` +
                `Joel from DefDossier here. Saw you asked to extend the trial for ${l.org || "your team"}.\n\n` +
                `Quick call to talk through what you're trying to get done by the original end date?\n\n` +
                `— Joel`
              )}` : `mailto:${l.email}?subject=${encodeURIComponent(
                "Following up on your DefDossier sample pack"
              )}&body=${encodeURIComponent(
                `Hi ${l.name?.split(" ")[0] || "there"},\n\n` +
                `Joel from DefDossier here. I saw you grabbed the sample compliance pack` +
                (l.org ? ` for ${l.org}` : "") + ` — thanks for taking a look.\n\n` +
                `Quick question: what's the tender-evidence binder process at your end right now? ` +
                `Spreadsheet, doc, or something else?\n\n` +
                `Happy to walk you through a 30-day pilot if it's useful.\n\n— Joel`
              )}`;
              return (
                <div key={l.id} className="py-3 flex items-center justify-between gap-3 flex-wrap"
                     data-testid={`lead-row-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-white truncate flex items-center gap-2">
                      {isExtension && (
                        <span className="font-mono text-[9px] tracking-widest uppercase border border-[var(--amber)] text-[var(--amber)] px-1.5 py-0.5"
                              data-testid={`lead-tag-${l.id}`}>
                          EXTEND
                        </span>
                      )}
                      <span className="truncate">{l.email}</span>
                    </div>
                    <div className="font-mono text-[10px] text-[var(--text-2)] mt-1 truncate">
                      {l.name}{l.org && <> · <span className="text-[var(--cyan)]">{l.org}</span></>}{l.role && <> · {l.role}</>}{isExtension && l.trial_days_left != null && <> · {l.trial_days_left}d left</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {ago && <span className="font-mono text-[10px] text-[var(--text-2)] tracking-widest uppercase">{ago}</span>}
                    <a href={mailto} className="btn-ghost text-xs inline-flex items-center gap-1"
                       data-testid={`lead-email-${l.id}`}>
                      <Mail size={11} /> Reply
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
}
